import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export type Connection = {
  /**
   * The id of the dashboard member
   */
  dashboardMemberId: number;
  /**
   * The memberId of the underlying member if the type is not EXTERNAL_MEMBER.
   */
  memberId?: number;
  /**
   * The externalMemberId if the type is EXTERNAL_MEMBER.
   */
  externalMemberId?: number;
  /**
   * Id of the user if the type is not EXTERNAL_MEMBER.
   */
  userId?: number;
  fields: {
    FIRST_NAME: string;
    LAST_NAME: string;
    [key: string]: string;
  };
  tags: string[];
  type: ConnectionType;
  note: string;
  verificationLabel: string;
  active: boolean;
  inactiveReason?: string;
  inviteSent?: boolean;
  /**
   * Present when the external member was linked into this (subcommunity) dashboard from the
   * parent business. Contains the id of the root external member holding the data.
   */
  parentExternalMemberId?: number;
  picture?: string;
  financeAdministrationStartAmount?: number;
};

export enum ConnectionType {
  EXTERNAL_MEMBER = 'EXTERNAL_MEMBER',
  MEMBER = 'MEMBER'
}

const initalState: Connection[] = [];

const connectionsSlice = createSlice({
  name: 'connections',
  initialState: initalState,
  reducers: {
    /*
     * Sets the connections in the store
     * @param {ConnectionType[]} state - The current state
     * @param {PayloadAction<ConnectionType[]>} action - The action containing the new state
     * @returns {void}
     */
    setConnections: (state, action: PayloadAction<Connection[]>): void => {
      state.splice(0, state.length, ...action.payload);
    },

    deleteConnections: (
      state,
      action: PayloadAction<{ dashboardMembersIds: number[]; }>,
    ): void => {
      const { dashboardMembersIds } = action.payload;
      const newState = state.filter(
        (connection) => !(dashboardMembersIds.includes(connection.dashboardMemberId)),
      );
      state.splice(0, state.length, ...newState);
    },

    assignTagToConnections: (
      state,
      action: PayloadAction<{ tag: string; connections: number[]; type: ConnectionType }>,
    ): void => {
      const { tag, connections, type } = action.payload;
      connections.forEach((id): void => {
        const index = state.findIndex(
          (connection): boolean => connection.dashboardMemberId === id && connection.type === type,
        );
        if (index !== -1 && !state[index].tags.includes(tag)) state[index].tags.push(tag);
      });
    },

    removeTagFromConnection(
      state,
      action: PayloadAction<{ tag: string; connection: number; type: ConnectionType }>,
    ): void {
      const { tag, connection, type } = action.payload;
      const index = state.findIndex(
        (c): boolean => c.dashboardMemberId === connection && c.type === type && c.tags.includes(tag),
      );
      if (index !== -1) state[index].tags.splice(state[index].tags.indexOf(tag), 1);
    },

    removeTagFromAllConnections: (state, action: PayloadAction<string>): void => {
      const tag = action.payload;
      state.forEach((connection): void => {
        const index = connection.tags.findIndex((t): boolean => t === tag);
        if (index !== -1) connection.tags.splice(index, 1);
      });
    },

    addConnections: (state, action: PayloadAction<Connection[]>): void => {
      state.push(...action.payload);
    },

    updateConnection: (state, action: PayloadAction<Connection>): void => {
      const index = state.findIndex(
        (connection): boolean =>
          connection.dashboardMemberId === action.payload.dashboardMemberId && connection.type === action.payload.type,
      );
      if (index !== -1) state[index] = action.payload;
    },

    updateNote(
      state,
      action: PayloadAction<{ id: number; type: ConnectionType; note: string }>,
    ): void {
      const { id, type, note } = action.payload;
      const index = state.findIndex(
        (connection): boolean => connection.dashboardMemberId === id && connection.type === type,
      );
      if (index !== -1) state[index].note = note;
    },

    updateInitialBalance(
      state,
      action: PayloadAction<{ id: number; financeAdministrationStartAmount: number }>,
    ): void {
      const { id, financeAdministrationStartAmount } = action.payload;
      const index = state.findIndex(
        (connection): boolean => connection.dashboardMemberId === id,
      );
      if (index !== -1) state[index].financeAdministrationStartAmount = financeAdministrationStartAmount;
    },

    setContactsToInviteSent: (state, action: PayloadAction<number[]>): void => {
      const ids = action.payload;
      ids.forEach((id): void => {
        const index = state.findIndex(
          (connection): boolean =>
            connection.externalMemberId === id && connection.type === ConnectionType.EXTERNAL_MEMBER,
        );
        if (index !== -1) state[index].inviteSent = true;
      });
    },

    updateMemberStatus: (
      state,
      action: PayloadAction<{
        id: number;
        type: ConnectionType;
        active: boolean;
        inactiveReason?: string;
      }>,
    ): void => {
      const { id, type, active, inactiveReason } = action.payload;
      const index = state.findIndex(
        (connection): boolean => connection.dashboardMemberId === id && connection.type === type,
      );
      if (index !== -1) {
        state[index].active = active;
        state[index].inactiveReason = inactiveReason;
      }
    },
  },
});

export const {
  setConnections,
  deleteConnections,
  assignTagToConnections,
  removeTagFromAllConnections,
  addConnections,
  updateConnection,
  removeTagFromConnection,
  updateNote,
  updateInitialBalance,
  setContactsToInviteSent,
  updateMemberStatus,
} = connectionsSlice.actions;
export default connectionsSlice.reducer;
